import React, { useCallback, useEffect, useState, useRef, MouseEvent } from "react";
import { Modal, Button, FlashMessage } from "../../../components/common";
import {
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from "@material-ui/icons";
import _ from "lodash";
import { MIN_VIDEO_DURATION_SEC } from "../../../utils/appConstants";
import Image from "../../../components/common/Image";
import { isRecordedVideoClip, TIMELINE_THUMB_HEIGHT } from "../videoStudioTimeline.utils";

type IVideoTrimModal = {
  show: boolean;
  videoSrc: any;
  onClose: () => void;
  onVideoCut: (effect: any) => void;
};

interface IDefaultPos {
  height: number;
  left: number;
  width?: number;
}

const defaultPos: IDefaultPos = {
  height: TIMELINE_THUMB_HEIGHT,
  left: 0,
  width: 0,
};

const TRIM_HANDLE_WIDTH = 4;

const VideoTrimModal = (props: IVideoTrimModal) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trimBarWrapRef = useRef<HTMLDivElement>(null);
  const videoThumbBar = useRef<HTMLDivElement>(null);
  const leftPosRef = useRef<HTMLDivElement>(null);
  const rightPosRef = useRef<HTMLDivElement>(null);
  const [thumbnails, setThumbnails] = useState<Array<any>>([]);
  const [thumbTrackWidth, setThumbTrackWidth] = useState(0);
  const [leftPos, setLeftPos] = useState<IDefaultPos>(defaultPos);
  const [leftMask, setLeftMask] = useState<IDefaultPos>(defaultPos);
  const [rightPos, setRightPos] = useState<IDefaultPos>(defaultPos);
  const [rightMask, setRightMask] = useState<IDefaultPos>(defaultPos);

  const [leftPosIsDown, setLeftPosIsDown] = useState<boolean>(false);
  const [rightPosIsDown, setRightPosIsDown] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);

  const [startTime, setStartTime] = useState<number>(0);
  const [endTime, setEndTime] = useState<number>(0);
  const [thumbStartTime, setThumbStartTime] = useState<number>(0);
  const [thumbEndTime, setThumbEndTime] = useState<number>(0);

  const mirrorPreview = isRecordedVideoClip(props.videoSrc);

  const getSourceDuration = useCallback(
    () =>
      Number(
        props.videoSrc?.sourceDuration ??
          props.videoSrc?.metaData?.sourceDuration ??
          props.videoSrc?.duration ??
          0
      ),
    [props.videoSrc]
  );

  const getMaxHandleLeft = (trackWidth: number) =>
    Math.max(trackWidth - TRIM_HANDLE_WIDTH, 0);

  const timeToHandleLeft = (
    time: number,
    thumbStart: number,
    thumbEnd: number,
    trackWidth: number
  ) => {
    const span = Math.max(thumbEnd - thumbStart, 0.001);
    const ratio = (time - thumbStart) / span;
    return clampPosition(
      ratio * getMaxHandleLeft(trackWidth),
      trackWidth
    );
  };

  const applyHandlePositions = (
    absoluteStart: number,
    absoluteEnd: number,
    thumbStart: number,
    thumbEnd: number,
    trackWidth: number
  ) => {
    const safeEnd = Math.max(absoluteEnd, absoluteStart);
    const leftPx = timeToHandleLeft(
      absoluteStart,
      thumbStart,
      thumbEnd,
      trackWidth
    );
    const rightPx = timeToHandleLeft(safeEnd, thumbStart, thumbEnd, trackWidth);

    setLeftPos((prevState) => ({ ...prevState, left: leftPx }));
    setLeftMask((prevState) => ({
      ...prevState,
      left: 0,
      width: leftPx,
    }));
    setRightPos((prevState) => ({ ...prevState, left: rightPx }));
    setRightMask((prevState) => ({
      ...prevState,
      left: rightPx,
      width: Math.max(trackWidth - rightPx, 0),
    }));
  };

  const measureThumbTrackWidth = useCallback(() => {
    const wrapWidth = trimBarWrapRef.current?.clientWidth || 0;
    if (wrapWidth > 0) {
      return wrapWidth;
    }

    return videoThumbBar.current?.clientWidth || 0;
  }, []);

  const clampPosition = (value: number, trackWidth: number) =>
    Math.max(0, Math.min(value, Math.max(trackWidth - TRIM_HANDLE_WIDTH, 0)));

  const applyDefaultTrimPositions = useCallback(
    (trackWidth: number, sourceDuration: number) => {
      setThumbStartTime(0);
      setThumbEndTime(sourceDuration);
      setStartTime(0);
      setEndTime(sourceDuration);
      applyHandlePositions(0, sourceDuration, 0, sourceDuration, trackWidth);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const applySavedTrimPositions = useCallback(
    (metaData: any, trackWidth: number, sourceDuration: number) => {
      const thumbStart =
        metaData.thumbStartTime ?? metaData.startTime ?? 0;
      const thumbEnd =
        metaData.thumbEndTime ??
        metaData.endTime ??
        sourceDuration;
      const absoluteStart = metaData.startTime ?? thumbStart;
      const absoluteEnd = metaData.endTime ?? thumbEnd;

      setThumbStartTime(thumbStart);
      setThumbEndTime(thumbEnd);
      setStartTime(absoluteStart);
      setEndTime(absoluteEnd);
      applyHandlePositions(
        absoluteStart,
        absoluteEnd,
        thumbStart,
        thumbEnd,
        trackWidth
      );

      window.setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.currentTime = absoluteStart;
          setPlaybackTime(absoluteStart);
        }
      }, 300);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!props.show) {
      return;
    }

    const handleMouseUp = () => {
      setLeftPosIsDown(false);
      setRightPosIsDown(false);
    };

    document.addEventListener("mouseup", handleMouseUp);
    setIsPlaying(false);
    setPlaybackTime(0);
    setThumbnails(props.videoSrc.thumbnails || []);

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, props.videoSrc]);

  useEffect(() => {
    if (!props.show || !thumbnails.length) {
      setThumbTrackWidth(0);
      return;
    }

    const syncTrackLayout = () => {
      const trackWidth = measureThumbTrackWidth();
      if (!trackWidth) {
        return;
      }

      setThumbTrackWidth(trackWidth);

      const sourceDuration = getSourceDuration();
      const metaData = _.get(props, "videoSrc.metaData", {});
      if (!_.isEmpty(metaData)) {
        applySavedTrimPositions(metaData, trackWidth, sourceDuration);
      } else {
        applyDefaultTrimPositions(trackWidth, sourceDuration);
      }
    };

    const frame = window.requestAnimationFrame(syncTrackLayout);
    window.addEventListener("resize", syncTrackLayout);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(syncTrackLayout)
        : null;

    if (resizeObserver && trimBarWrapRef.current) {
      resizeObserver.observe(trimBarWrapRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", syncTrackLayout);
      resizeObserver?.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.show,
    props.videoSrc,
    thumbnails,
    measureThumbTrackWidth,
    applyDefaultTrimPositions,
    applySavedTrimPositions,
    getSourceDuration,
  ]);

  const handleOnClose = () => {
    const { onClose } = props;
    if (typeof onClose === "function") {
      onClose();
    }

    resetModal();
  };

  const _handleCutVideo = async () => {
    if (endTime - startTime >= MIN_VIDEO_DURATION_SEC) {
      const { videoSrc, onVideoCut } = props;

      const updatedEffect = {
        ...videoSrc,
        metaData: {
          startTime,
          endTime,
          sourceDuration: getSourceDuration(),
          thumbStartTime,
          thumbEndTime,
          trackWidth: thumbTrackWidth,
        },
      };

      if (typeof onVideoCut === "function") {
        onVideoCut(updatedEffect);
      }

      handleOnClose();
    } else {
      FlashMessage(
        `Minimum duration must be ${MIN_VIDEO_DURATION_SEC}sec to cut the video`,
        "error"
      );
    }
  };

  const _handleTrimVideo = (event: MouseEvent<HTMLDivElement>): void => {
    const { currentTarget: current } = event;

    event.preventDefault();

    const trackWidth = thumbTrackWidth || current.getBoundingClientRect().width;
    if (!trackWidth) {
      return;
    }

    let currentTime = 0;
    const clientRect = current.getBoundingClientRect();
    const thumbSpan = Math.max(thumbEndTime - thumbStartTime, 0.001);
    const width = clientRect.width;

    if (leftPosIsDown || rightPosIsDown) {
      const ratio = (event.clientX - clientRect.x) / width;
      currentTime = thumbStartTime + ratio * thumbSpan;
    }

    if (leftPosIsDown) {
      const clampedStart = Math.max(
        thumbStartTime,
        Math.min(currentTime, endTime)
      );
      const leftBarPos = timeToHandleLeft(
        clampedStart,
        thumbStartTime,
        thumbEndTime,
        width
      );

      setLeftPos((prevState) => ({ ...prevState, left: leftBarPos }));
      setLeftMask((prevState) => ({ ...prevState, width: leftBarPos }));
      setStartTime(clampedStart);
      if (videoRef.current) {
        videoRef.current.currentTime = clampedStart;
        setPlaybackTime(clampedStart);
      }
    }

    if (rightPosIsDown) {
      const clampedEnd = Math.max(
        startTime,
        Math.min(currentTime, thumbEndTime)
      );
      const rightBarPos = timeToHandleLeft(
        clampedEnd,
        thumbStartTime,
        thumbEndTime,
        width
      );

      setRightPos((prevState) => ({ ...prevState, left: rightBarPos }));
      setRightMask((prevState) => ({
        ...prevState,
        left: rightBarPos,
        width: Math.max(width - rightBarPos, 0),
      }));
      setEndTime(clampedEnd);
      if (videoRef.current && videoRef.current.currentTime > clampedEnd) {
        videoRef.current.currentTime = clampedEnd;
        setPlaybackTime(clampedEnd);
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleTogglePlay = () => {
    if (!videoRef.current) {
      return;
    }

    if (isPlaying) {
      videoRef.current.pause();
      return;
    }

    const video = videoRef.current;
    if (video.currentTime < startTime || video.currentTime >= endTime) {
      video.currentTime = startTime;
      setPlaybackTime(startTime);
    }

    void video.play();
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const current = video.currentTime;

    if (current >= endTime) {
      video.pause();
      video.currentTime = endTime;
      setPlaybackTime(endTime);
      setIsPlaying(false);
      return;
    }

    if (current < startTime) {
      video.currentTime = startTime;
      setPlaybackTime(startTime);
      return;
    }

    setPlaybackTime(current);
  };

  const handlePlaybackSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextTime = Math.max(
      startTime,
      Math.min(endTime, Number(event.target.value))
    );
    setPlaybackTime(nextTime);

    if (videoRef.current) {
      videoRef.current.currentTime = nextTime;
    }
  };

  useEffect(() => {
    if (!props.show || !videoRef.current) {
      return;
    }

    const video = videoRef.current;
    if (video.currentTime < startTime || video.currentTime > endTime) {
      video.currentTime = startTime;
      setPlaybackTime(startTime);
      video.pause();
      setIsPlaying(false);
    }
  }, [props.show, startTime, endTime]);

  const resetModal = () => {
    setThumbnails([]);
    setThumbTrackWidth(0);
    setLeftPos(defaultPos);
    setLeftMask(defaultPos);
    setRightPos(defaultPos);
    setRightMask(defaultPos);
    setLeftPosIsDown(false);
    setRightPosIsDown(false);
    setIsPlaying(false);
    setPlaybackTime(0);
    setStartTime(0);
    setEndTime(0);
    setThumbStartTime(0);
    setThumbEndTime(0);
  };

  const seekValue = Math.max(startTime, Math.min(endTime, playbackTime));
  const seekRange = Math.max(endTime - startTime, 0.01);
  const seekProgressPercent = Math.min(
    100,
    Math.max(0, ((seekValue - startTime) / seekRange) * 100)
  );

  return (
    <Modal
      visible={props.show}
      size="x-large"
      closeOnBackDrop={true}
      onClose={() => handleOnClose()}
      className="video-trim-model"
    >
      <div>
        <div className="video-trim-player">
          <div
            className={`video-trim-player-media ${
              mirrorPreview ? "video-trim-player-media--mirror" : ""
            }`}
          >
            <video
              ref={videoRef}
              className="video-trim-player-video"
              src={props.videoSrc.src}
              playsInline
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onTimeUpdate={handleTimeUpdate}
            />
          </div>
          <div className="video-trim-player-controls">
            <button
              type="button"
              className="video-trim-player-play-btn"
              onClick={handleTogglePlay}
              aria-label={isPlaying ? "Pause video" : "Play video"}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </button>
            <input
              type="range"
              className="video-trim-player-seek"
              min={startTime}
              max={Math.max(endTime, startTime)}
              step={0.01}
              value={seekValue}
              onChange={handlePlaybackSeek}
              aria-label="Seek trimmed video"
              style={
                {
                  "--seek-progress": `${seekProgressPercent}%`,
                } as React.CSSProperties
              }
            />
          </div>
        </div>
        <div className="video-duration text-center">
          <p>
            <span>Start Time:</span> {startTime.toFixed(2)}sec
          </p>
          <span>&nbsp;|&nbsp;</span>
          <p>
            <span>End Time:</span> {endTime.toFixed(2)}sec
          </p>
          <span>&nbsp;|&nbsp;</span>
          <p>
            <span>Duration:</span>{" "}
            {endTime - startTime <= 0
              ? "0.00"
              : (endTime - startTime).toFixed(2)}
            sec
          </p>
        </div>
        <div className="video-trim-bar-wrap" ref={trimBarWrapRef}>
          <div className="video-trim-bar" onMouseMove={_handleTrimVideo}>
            <div
              id="leftMask"
              className="pre-asset"
              style={{
                height: leftMask.height,
                left: leftMask.left,
                width: leftMask.width,
              }}
            />
            <div
              id="leftPos"
              ref={leftPosRef}
              onMouseDown={() => setLeftPosIsDown(true)}
              className="pre-asset"
              style={{ height: leftPos.height, left: leftPos.left }}
            />
            <div
              id="rightPos"
              ref={rightPosRef}
              onMouseDown={() => setRightPosIsDown(true)}
              className="pre-asset"
              style={{ height: rightPos.height, left: rightPos.left }}
            />
            <div
              id="rightMask"
              className="pre-asset"
              style={{
                height: rightMask.height,
                left: rightMask.left,
                width: rightMask.width,
              }}
            />

            <div
              ref={videoThumbBar}
              className={`video-thumbs-bar ${
                mirrorPreview ? "video-thumbs-bar--mirror" : ""
              }`}
            >
              {(thumbnails || []).map((thumb, key) => (
                <Image
                  key={key}
                  className="video-trim-thumb"
                  height={`${TIMELINE_THUMB_HEIGHT}`}
                  src={URL.createObjectURL(thumb.blob)}
                  alt="video thumbnails"
                />
              ))}
            </div>
          </div>
        </div>
        <div className="text-center bottom-control">
          <Button
            color="dark-pink"
            className="vs-button-settings"
            onClick={() => _handleCutVideo()}
          >
            Trim
          </Button>
          <Button
            color="dark-pink"
            className="vs-button-settings btn-transparent"
            onClick={() => handleOnClose()}
          >
            Discard
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default VideoTrimModal;
